import {
  allPass,
  any,
  filter,
  pipe,
  isEmpty,
  anyPass,
  pathEq,
  prop,
  includes,
  values,
  either,
  reject,
  find,
  path,
  pathOr,
  has,
  propOr,
  gt,
  lt,
  __,
  equals,
  propEq,
  complement,
  isNil,
  memoizeWith
} from 'ramda'
import { utils } from '@digital-commerce/shared-utils'
import { getDiscountDisplayName } from '../../../utils/discount'
import {
  getConnectionFee,
  getEasyRoamOffers,
  getMobilityOffersWithItems,
  getVASAddOns,
  getConnectionFeeAttribute,
  getMobVASAttributeList,
  getItemRecurringDiscounts,
  getItemsNonRecurringDiscounts,
  getMobilityWarranty,
  getTerm,
  getLastMobilityAddon,
  getItemRecurringCredits,
  getOrbitItemFromOffer,
  getOrbitDeviceAttribute,
  getSimItem,
  getSimTypeAttribute,
  isMobilePhone
} from '../../../utils/mobility'
import { formatCurrency } from '../../../utils/currency'
import {
  buildConnectionFeeData,
  buildMobilityMonthlyEasyPaymentPrice,
  getColourAttributeText,
  getStorageAttributeText
} from '../../../utils/display'
import { getCacheKey } from '../../../utils/cart'

const { getDisplayName, getMonthlyItemPrice, getItemOneTimePrice } = utils.cart

const _isOrbitDevice = pipe(
  prop('category'),
  either(equals('MOBILITY_TRADE_IN'), equals('MOBILITY_BRING_IT_BACK'))
)

export const isWirelessHomePhone = allPass([
  propEq(['category'], 'MOBILITY_PHONE'),
  complement(
    anyPass([
      pipe(prop('tags'), includes('smartwatch')),
      pipe(prop('tags'), includes('connected_products'))
    ])
  ),
  pipe(prop('tags'), includes('homephone'))
])

const isPhone = anyPass([isMobilePhone, isWirelessHomePhone])

const hasOrbitOffer = pipe(prop('items'), values, any(_isOrbitDevice))

const hasMobilePhoneOffer = pipe(prop('items'), values, any(isMobilePhone))

const hasMobilityWirelessHomePhoneOffer = pipe(prop('items'), values, any(isWirelessHomePhone))

const getItemNonRecurringDiscounts = (item, term) =>
  pipe(
    pathOr([], ['itemPrice', 'recurring']),
    find(has('alterations')),
    propOr([], 'alterations'),
    filter(
      allPass([
        pathEq(['category'], 'DISCOUNT'),
        pipe(path(['recurringDuration']), gt(__, 1)),
        pipe(path(['recurringDuration']), lt(__, term))
      ])
    )
  )(item)

const buildDiscountMapping = (discounts, lang, item = {}, recurring = true) => {
  const recurringSuffix = recurring ? '/mo.' : ''

  const mappedDiscountDataList = discounts?.map((discount) => {
    const id = discount.id || discount.promotionId || item.id
    const hasCouponCode = discount.isCouponCode
    const prefixIcon = hasCouponCode ? 'PIGGYBANK' : 'DEALS'
    return {
      id,
      name: getDiscountDisplayName(discount),
      price: `-${formatCurrency(discount.amount, lang)}${recurringSuffix}`,
      prefixIcon
    }
  })

  return mappedDiscountDataList
}

export const getMobilityPhoneOffers = memoizeWith(
  getCacheKey('MOB-Phone-Offers'),
  pipe(getMobilityOffersWithItems, filter(hasMobilePhoneOffer), reject(hasOrbitOffer))
)

export const getMobilityWirelessHomePhoneOffers = memoizeWith(
  getCacheKey('MOB-Wireless-Home-Phone'),
  pipe(getMobilityOffersWithItems, filter(hasMobilityWirelessHomePhoneOffer), reject(hasOrbitOffer))
)

export const getOrbitMobilityPhoneOffers = memoizeWith(
  getCacheKey('MOB-Orbit-Phone'),
  pipe(getMobilityOffersWithItems, filter(allPass([hasMobilePhoneOffer, hasOrbitOffer])))
)

export const getPhoneItem = (offer) => pipe(prop('items'), values, find(isPhone))(offer)

export const getAttributes = (offer, phone, plan, isMobilityWirelessHomePhone, translate, lang) => {
  const attributesList = []

  const mobilityVAS = getVASAddOns(offer)
  const warranty = getMobilityWarranty(offer)
  const connectionFee = getConnectionFee(offer)
  const easyRoam = getEasyRoamOffers(offer)
  const simItem = getSimItem(offer)

  // Storage and Colour
  const storageAttributeText = getStorageAttributeText(phone, translate)
  const colourAttributeText = getColourAttributeText(phone, translate)
  const deviceStorageAndColourLabel = storageAttributeText
    ? `${storageAttributeText}, ${colourAttributeText}`
    : `${colourAttributeText}`
  attributesList.push(deviceStorageAndColourLabel)

  // Plan
  const displayNameLabel = isMobilityWirelessHomePhone
    ? getDisplayName(plan)
    : `${getDisplayName(plan)}, ${getDisplayName(offer)}`
  const easyRoamLabel = isEmpty(easyRoam) ? '' : `, ${translate('item.easyRoam')}`
  const planNameLabel = `${displayNameLabel}${easyRoamLabel}`
  attributesList.push(planNameLabel)

  // Warranty
  if (warranty) {
    attributesList.push(getDisplayName(warranty))
  }

  // SIM
  const simTypeAttribute =
    simItem &&
    simItem.quantity > 0 &&
    getSimTypeAttribute(simItem, offer, lang, translate)

  if (simTypeAttribute && !isMobilityWirelessHomePhone) attributesList.push(simTypeAttribute)

  // Connection Fee
  if (connectionFee) {
    attributesList.push(getConnectionFeeAttribute(connectionFee, translate))
  }

  // VAS
  attributesList.push(...getMobVASAttributeList(mobilityVAS, translate))

  // Orbit
  const orbitOffer = getOrbitItemFromOffer(offer)
  if (orbitOffer) {
    attributesList.push(...getOrbitDeviceAttribute(orbitOffer, translate))
  }

  return attributesList
}

// ------ Offer Details ------

export const getOneTimeEasyPaymentPriceBreakdown = (device, translate, lang) => {
  const oneTimeFinancedAmountCredit = pipe(
    pathOr([], ['itemPrice', 'oneTime']),
    find(allPass([has('alterations')])),
    propOr([], 'alterations'),
    find(pathEq(['type'], 'financedAmount')),
    prop('amount')
  )(device)

  if (!oneTimeFinancedAmountCredit) return null

  const { duration, amount } = getMonthlyItemPrice(device)
  const name = translate('item.easyPaymentBalance', {
    replacements: { value: amount, duration }
  })

  return {
    id: 'easy-payment-balance',
    name,
    price: `-${formatCurrency(oneTimeFinancedAmountCredit, lang)}`
  }
}

export const buildMonthlyEasyPaymentPrice = ({
  isMobilityWirelessHomePhone,
  device,
  translate,
  lang
}) => {
  if (isMobilityWirelessHomePhone) return null

  return buildMobilityMonthlyEasyPaymentPrice({ device, translate, lang })
}

export const buildRecurringDiscountList = (offer, plan, lang) => {
  const term = getTerm(plan)

  const offerDiscounts = getItemRecurringDiscounts(offer, term)
  const planDiscounts = getItemRecurringDiscounts(plan, term)

  const mappedPlanDiscounts = buildDiscountMapping(planDiscounts, lang, plan)
  const mappedOfferDiscounts = buildDiscountMapping(offerDiscounts, lang, offer)

  return [...mappedPlanDiscounts, ...mappedOfferDiscounts]
}

export const buildWarrantyInfoPrice = (offer, lang) => {
  const warranty = getMobilityWarranty(offer)

  if (!warranty) return null

  const warrantyMonthlyPrice = getMonthlyItemPrice(warranty).amount
  return {
    id: warranty.id,
    name: getDisplayName(warranty),
    price: `${formatCurrency(warrantyMonthlyPrice, lang)}/mo.`
  }
}

// Gets one-time sim card fee
const buildNonVASMobilityAddon = (offer, translate, lang) => {
  const addon = getLastMobilityAddon(offer)

  if (!addon) return null

  return {
    id: addon.id,
    name: translate('item.mobilityAddOn'),
    price: formatCurrency(getMonthlyItemPrice(addon).amount, lang)
  }
}

export const buildOtherChargesList = (offer, translate, lang) => {
  const chargesList = []
  chargesList.push(buildConnectionFeeData(offer, translate, lang))

  const oneTimeSimCardFee = buildNonVASMobilityAddon(offer, translate, lang)
  if (oneTimeSimCardFee) chargesList.push(oneTimeSimCardFee)

  return chargesList
}

export const buildCreditsAndDiscountsList = (offer, plan, lang, isOrbit) => {
  const term = getTerm(plan)

  const planNonRecurringDiscounts = getItemNonRecurringDiscounts(plan, term)
  const planRecurringCredits = getItemRecurringCredits(plan)
  const nonRecurringVASDiscounts = getItemsNonRecurringDiscounts(getVASAddOns(offer, term))

  if (isOrbit) {
    const offerRecurringCredits = getItemRecurringCredits(offer)

    const orbitCreditsAndDiscounts = [
      ...planNonRecurringDiscounts,
      ...planRecurringCredits,
      ...offerRecurringCredits,
      ...nonRecurringVASDiscounts
    ]

    return buildDiscountMapping(orbitCreditsAndDiscounts, lang, {}, false)
  }

  const creditsAndDiscounts = [
    ...planNonRecurringDiscounts,
    ...planRecurringCredits,
    ...nonRecurringVASDiscounts
  ]

  return buildDiscountMapping(creditsAndDiscounts, lang, {}, false)
}

/**
 * *** This calculation is required for mobility phone ***
 * Be aware when refactoring
 * */
export const calculateDueTodaySubTotal = (item) => {
  const { subTotal, credit } = getItemOneTimePrice(item) || {}

  return subTotal - credit
}

export const getFinancedAccessoryGroupFromOffer = (offer, accessoryGroups) => {
  return accessoryGroups?.find(
    (accessoryGroup) => accessoryGroup?.financeDetails?.[0].relatedItemId === offer?.id
  )
}
export const shouldRenderMobilityPhone = (cart) => {
  const offers = getMobilityPhoneOffers(cart)
  return !isNil(offers) && !isEmpty(offers)
}

export const shouldRenderMobilityWirelessHomePhone = (cart) => {
  const offers = getMobilityWirelessHomePhoneOffers(cart)
  return !isNil(offers) && !isEmpty(offers)
}

export const shouldRenderOrbitMobilityPhone = (cart) => {
  const offers = getOrbitMobilityPhoneOffers(cart)
  return !isNil(offers) && !isEmpty(offers)
}
